import { MediaContentTypes } from "constants/contentTypes"
import { HyperLinkBuilder } from "widgets/commonComponents/inline/hyperLink"
import { PayWallArctilceIcon } from "widgets/commonComponents/inline/payWallHavak/payWallHavak"
import { LinkedItemFieldDisplay } from "widgets/commonDataObjects/linkedItemsFieldsDisplay"
import { LinkedItemType, VideoSettingsData } from "widgets/commonDataObjects/interfaces"
import { SiteImageData, SiteMediaData, SiteVideoData } from "widgets/interfaces"
import { HotStoriesMedia } from "./hotStoriesMedia"
import { separateHourFromDateAndFormatToString } from "widgets/widgetsUtils"
import { getDiffFromToday } from "../../../../siteWidgets/formatDateDisplay"
import * as classNames from "classnames"
import Slider from "react-slick"
import { HOT_STORIES_BG, syncHotStoriesTitleSubClamp } from "../hotStoriesConsts"
import { HotStoriesSiteItem } from "./hotStoriesFront"

interface HotStoriesFrontMobileProps {
    items: HotStoriesSiteItem[]
    width: number
    videoSettings: VideoSettingsData
    fieldsDisplaySettings: LinkedItemFieldDisplay
    gallerySeconds: number
    backgroundColorComponent?: string
    backgroundColorRoof?: string
    backgroundColorLabel?: string
}

interface HotStoriesFrontMobileState {
    activeIndex: number
    isMuted: boolean
    autoplayEnabled: boolean
    progressKey: number
}

export class HotStoriesFrontMobile extends React.Component<HotStoriesFrontMobileProps, HotStoriesFrontMobileState> {
    public static siteScriptName = "HotStoriesFrontMobile"

    private autoTimer: any
    private mediaWidth = 640
    private mediaHeight = 364
    private progressPollTimer: any = null
    private componentRootRef: HTMLDivElement | null = null
    private mediaRootRef: HTMLDivElement | null = null
    private videoProgressFillRef: HTMLDivElement | null = null
    private lastProgressRatio = 0
    private slider: any = null
    private isProgrammaticSlide = false
    private pendingStopAutoplay = false
    private titleClampRaf: number | null = null

    constructor(props: HotStoriesFrontMobileProps) {
        super(props)
        this.state = {
            activeIndex: 0,
            isMuted: true,
            autoplayEnabled: props.gallerySeconds > 0,
            progressKey: 0,
        }
    }

    componentDidMount() {
        this.scheduleAutoAdvance()
        this.startVideoProgressPoll()
        this.scheduleTitleSubClamp()
        window.YitVideo && window.YitVideo.InfrastructureReInitialize()
    }

    componentDidUpdate(_: HotStoriesFrontMobileProps, prevState: HotStoriesFrontMobileState) {
        if (prevState.activeIndex !== this.state.activeIndex) {
            this.stopVideoProgressPoll()
            this.lastProgressRatio = 0
            window.YitVideo && window.YitVideo.InfrastructureReInitialize()
            this.scheduleAutoAdvance()
            this.startVideoProgressPoll()
            this.scheduleTitleSubClamp()
        } else if (prevState.isMuted !== this.state.isMuted || prevState.autoplayEnabled !== this.state.autoplayEnabled) {
            this.scheduleAutoAdvance()
            this.startVideoProgressPoll()
        }
    }

    componentWillUnmount() {
        this.clearAutoAdvance()
        this.stopVideoProgressPoll()
        if (this.titleClampRaf !== null && typeof window !== "undefined") {
            window.cancelAnimationFrame(this.titleClampRaf)
            this.titleClampRaf = null
        }
    }

    private clearAutoAdvance = () => {
        if (this.autoTimer) {
            clearTimeout(this.autoTimer)
            this.autoTimer = null
        }
    }

    private stopAutoplay = () => {
        this.clearAutoAdvance()
        if (this.state.autoplayEnabled) {
            this.setState({ autoplayEnabled: false })
        }
    }

    private isVideoItem = (item: HotStoriesSiteItem) => {
        const media = item && item.media
        if (!media) {
            return false
        }
        return media.mediaType === MediaContentTypes.MEDIA_VIDEO
    }

    private getAdvanceMs = (item: HotStoriesSiteItem) => {
        const { gallerySeconds } = this.props
        if (!gallerySeconds || gallerySeconds <= 0) {
            return 0
        }
        if (this.isVideoItem(item)) {
            return 0
        }
        return gallerySeconds * 1000
    }

    private scheduleAutoAdvance = () => {
        this.clearAutoAdvance()
        const { items } = this.props
        const { activeIndex, autoplayEnabled } = this.state
        if (!autoplayEnabled || !items || items.length < 2) {
            return
        }
        if (this.isVideoItem(items[activeIndex])) {
            return
        }
        const ms = this.getAdvanceMs(items[activeIndex])
        if (ms <= 0) {
            return
        }
        this.autoTimer = setTimeout(() => {
            this.goToIndex((activeIndex + 1) % items.length, false)
        }, ms)
    }

    private advanceFromVideo = () => {
        const { items } = this.props
        const { activeIndex, autoplayEnabled } = this.state
        if (!autoplayEnabled || !items || items.length < 2) {
            return
        }
        if (!this.isVideoItem(items[activeIndex])) {
            return
        }
        this.goToIndex((activeIndex + 1) % items.length, false)
    }

    private goToIndex = (index: number, stopAuto: boolean) => {
        const { items } = this.props
        if (!items || items.length === 0) {
            return
        }
        if (index === this.state.activeIndex) {
            if (stopAuto) {
                this.stopAutoplay()
            }
            return
        }
        if (stopAuto) {
            this.pendingStopAutoplay = true
        }
        if (this.slider && items.length > 1) {
            this.isProgrammaticSlide = true
            this.slider.slickGoTo(this.toSlickIndex(index))
            return
        }
        this.applyActiveIndex(index, stopAuto)
    }

    private onSelectItem = (index: number) => {
        this.goToIndex(index, true)
    }

    private onSliderSwipe = () => {
        this.pendingStopAutoplay = true
    }

    private toSlickIndex = (logicalIndex: number) => {
        const { items } = this.props
        return items.length - 1 - logicalIndex
    }

    private toLogicalIndex = (slickIndex: number) => {
        const { items } = this.props
        return items.length - 1 - slickIndex
    }

    private applyActiveIndex = (index: number, stopAutoplay: boolean) => {
        const { items } = this.props
        if (!items || index < 0 || index >= items.length) {
            return
        }
        if (index === this.state.activeIndex) {
            if (stopAutoplay) {
                this.stopAutoplay()
            }
            return
        }
        this.stopVideoProgressPoll()
        this.lastProgressRatio = 0
        this.setState(state => ({
            activeIndex: index,
            progressKey: state.progressKey + 1,
            autoplayEnabled: stopAutoplay ? false : state.autoplayEnabled,
        }))
    }

    private onBeforeChange = () => {
        this.clearAutoAdvance()
        this.stopVideoProgressPoll()
    }

    private onAfterChange = (index: number) => {
        this.isProgrammaticSlide = false
        const logical = this.toLogicalIndex(index)
        this.applyActiveIndex(logical, this.pendingStopAutoplay)
        this.pendingStopAutoplay = false
        this.scheduleTitleSubClamp()
        window.YitVideo && window.YitVideo.InfrastructureReInitialize()
    }

    private scheduleTitleSubClamp = () => {
        if (typeof window === "undefined") {
            return
        }
        if (this.titleClampRaf !== null) {
            window.cancelAnimationFrame(this.titleClampRaf)
        }
        this.titleClampRaf = window.requestAnimationFrame(() => {
            this.titleClampRaf = window.requestAnimationFrame(() => {
                this.titleClampRaf = null
                this.syncTitleSubClamp()
            })
        })
    }

    private setComponentRoot = (el: HTMLDivElement | null) => {
        this.componentRootRef = el
        this.scheduleTitleSubClamp()
    }

    private syncTitleSubClamp = () => {
        const root = this.componentRootRef
        if (!root) {
            return
        }
        const activeCopy = root.querySelector(".slick-slide.slick-active .hotStoriesCopy") as HTMLElement
        if (activeCopy) {
            syncHotStoriesTitleSubClamp(activeCopy)
            return
        }
        const stacks = root.querySelectorAll(".hotStoriesCopy")
        for (let i = 0; i < stacks.length; i++) {
            syncHotStoriesTitleSubClamp(stacks[i] as HTMLElement)
        }
    }

    private setMediaRoot = (el: HTMLDivElement | null) => {
        this.mediaRootRef = el
    }

    private setVideoProgressFill = (el: HTMLDivElement | null) => {
        this.videoProgressFillRef = el
        if (el) {
            el.style.width = `${Math.round(this.lastProgressRatio * 1000) / 10}%`
        }
    }

    private stopVideoProgressPoll = () => {
        if (this.progressPollTimer) {
            clearInterval(this.progressPollTimer)
            this.progressPollTimer = null
        }
    }

    private findActiveVideoEl = (): HTMLVideoElement | null => {
        const root = this.componentRootRef
        if (!root) {
            return null
        }
        const searchRoots: HTMLElement[] = []
        const activeSlide = root.querySelector(".slick-slide.slick-active") as HTMLElement
        if (activeSlide) {
            searchRoots.push(activeSlide)
        }
        if (this.mediaRootRef && searchRoots.indexOf(this.mediaRootRef) < 0) {
            searchRoots.push(this.mediaRootRef)
        }
        searchRoots.push(root)

        for (let r = 0; r < searchRoots.length; r++) {
            const videos = searchRoots[r].querySelectorAll("video")
            for (let i = 0; i < videos.length; i++) {
                const v = videos[i] as HTMLVideoElement
                if (!v.paused && v.duration > 0 && isFinite(v.duration)) {
                    return v
                }
            }
        }
        for (let r = 0; r < searchRoots.length; r++) {
            const videos = searchRoots[r].querySelectorAll("video")
            for (let i = 0; i < videos.length; i++) {
                const v = videos[i] as HTMLVideoElement
                if (v.duration > 0 && isFinite(v.duration)) {
                    return v
                }
            }
        }
        return null
    }

    private syncVideoProgressFromDom = () => {
        const fill = this.videoProgressFillRef
        const video = this.findActiveVideoEl()
        if (!fill || !video || !(video.duration > 0) || !isFinite(video.duration)) {
            return
        }
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || !this.isVideoItem(items[activeIndex])) {
            return
        }
        const time = video.currentTime
        if (!(time >= 0) || !isFinite(time)) {
            return
        }
        const next = Math.min(1, Math.max(0, time / video.duration))
        if (Math.abs(next - this.lastProgressRatio) < 0.005) {
            return
        }
        this.lastProgressRatio = next
        fill.style.width = `${Math.round(next * 1000) / 10}%`
    }

    private startVideoProgressPoll = () => {
        this.stopVideoProgressPoll()
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || !this.isVideoItem(items[activeIndex])) {
            return
        }
        this.progressPollTimer = setInterval(this.syncVideoProgressFromDom, 100)
    }

    private onPlayStarted = () => {
        this.clearAutoAdvance()
        const video = this.findActiveVideoEl()
        if (video && !video.muted && this.state.isMuted) {
            this.setState({ isMuted: false })
        }
        this.lastProgressRatio = 0
        this.startVideoProgressPoll()
        this.scheduleAutoAdvance()
    }

    private onVideoEnded = () => {
        this.lastProgressRatio = 1
        if (this.videoProgressFillRef) {
            this.videoProgressFillRef.style.width = "100%"
        }
        this.advanceFromVideo()
    }

    private onVideoError = () => {
        this.advanceFromVideo()
    }

    private formatMetaParts = (item: HotStoriesSiteItem) => {
        const { fieldsDisplaySettings } = this.props
        const author = fieldsDisplaySettings.isAuthorVisible && item.author ? item.author : ""
        let date = ""
        if (fieldsDisplaySettings.isDateVisible && item.itemType === LinkedItemType.ARTICLE && item.dateUpdatedOnSite) {
            const isTodayDate = getDiffFromToday(new Date(item.dateUpdatedOnSite)) === 0
            date = separateHourFromDateAndFormatToString(item.dateUpdatedOnSite, {
                showDate: !isTodayDate,
                showHour: isTodayDate,
            })
        }
        return { author, date }
    }

    private getActiveMedia = (item: HotStoriesSiteItem): SiteMediaData => {
        const media = item.media
        if (!media) {
            return { url: "", mediaType: MediaContentTypes.MEDIA_IMAGE, title: "", className: "", alt: "", ariaHidden: true } as SiteImageData
        }
        if (media.mediaType === MediaContentTypes.MEDIA_VIDEO) {
            return {
                ...media,
                isMuteAvailable: this.state.isMuted,
                isSoundButtonVisible: true,
            } as SiteVideoData
        }
        return media
    }

    private getSlideMedia = (item: HotStoriesSiteItem, isActive: boolean): SiteMediaData => {
        const media = this.getActiveMedia(item)
        if (!isActive && media.mediaType === MediaContentTypes.MEDIA_VIDEO) {
            const video = media as SiteVideoData
            return {
                url: video.poster || "",
                mediaType: MediaContentTypes.MEDIA_IMAGE,
                title: video.title || "",
                className: "",
                alt: "",
                ariaHidden: true,
                credit: video.credit,
            } as SiteImageData
        }
        return media
    }

    private renderStory = (item: HotStoriesSiteItem, index: number) => {
        const { fieldsDisplaySettings, videoSettings, backgroundColorRoof, backgroundColorLabel } = this.props
        const { activeIndex } = this.state
        const isActive = index === activeIndex
        const hasLink = !!(item.titleLink)
        const media = this.getSlideMedia(item, isActive)
        const isVideo = media.mediaType === MediaContentTypes.MEDIA_VIDEO
        const videoMedia = isVideo ? media as SiteVideoData : null
        const forcePlayVisible = !!(videoMedia && videoMedia.isAutoPlaying === false)
        const credit = (media as SiteImageData).credit
        const roofStyle = backgroundColorRoof ? { backgroundColor: backgroundColorRoof } : undefined
        const labelStyle = backgroundColorLabel ? { backgroundColor: backgroundColorLabel } : undefined
        const { author, date } = this.formatMetaParts(item)

        const titleNode = fieldsDisplaySettings.isTitleVisible && item.title && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <h2 className="hotStoriesTitle">{item.title}</h2>
                </HyperLinkBuilder>
                : <h2 className="hotStoriesTitle">{item.title}</h2>
        )

        const subTitleNode = fieldsDisplaySettings.isSubTitleVisible && item.subTitle && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesSubTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <p className="hotStoriesSubTitle">{item.subTitle}</p>
                </HyperLinkBuilder>
                : <p className="hotStoriesSubTitle">{item.subTitle}</p>
        )

        const cta = hasLink && item.buttonTitle
            ? <HyperLinkBuilder className="hotStoriesCta" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                <span className="hotStoriesCtaText">{item.buttonTitle.replace(/\s*>\s*$/, "").trim() || item.buttonTitle}</span>
            </HyperLinkBuilder>
            : null

        const authorNode = author && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesMetaAuthorLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <span className="hotStoriesMetaAuthor">{author}</span>
                </HyperLinkBuilder>
                : <span className="hotStoriesMetaAuthor">{author}</span>
        )

        const metaNode = (author || date) && (
            <div className="hotStoriesMeta">
                {authorNode}
                {author && date && <span className="hotStoriesMetaSep"> | </span>}
                {date && <span className="hotStoriesMetaDate">{date}</span>}
            </div>
        )

        return (
            <div className="hotStoriesStack" key={item.itemId || index} dir="rtl">
                <div
                    className={classNames("hotStoriesMedia", { forcePlayVisible })}
                    ref={isActive ? this.setMediaRoot : undefined}
                >
                    <HotStoriesMedia
                        data={media}
                        width={this.mediaWidth}
                        height={this.mediaHeight}
                        hideVideoAds={item.hideVideoAds !== false || !!(videoSettings && videoSettings.hideVideoAds)}
                        isImageLinkable={hasLink && !isVideo}
                        itemLinkUrl={item.titleLink}
                        linkTarget={item.titleTargetLink}
                        articleId={item.itemId}
                        onPlayStarted={isActive ? this.onPlayStarted : undefined}
                        onVideoEnded={isActive ? this.onVideoEnded : undefined}
                        onVideoError={isActive ? this.onVideoError : undefined}
                    />
                    {item.redLabelText &&
                        <span className="hotStoriesMediaLabel" style={labelStyle}>
                            {item.redLabelText}
                        </span>}
                    <PayWallArctilceIcon
                        premiumExpireDays={item.premiumExpireDays}
                        dateUpdatedOnSite={item.dateUpdatedOnSite}
                    />
                    {credit && <div className="hotStoriesCredit">({credit})</div>}
                </div>
                <div className="hotStoriesCopy">
                    <div className="hotStoriesArticleBody">
                        {fieldsDisplaySettings.isRoofTitleVisible && item.roofTitle &&
                            <span className="hotStoriesRoofChip" style={roofStyle}>{item.roofTitle}</span>}
                        {titleNode}
                        {subTitleNode}
                        {metaNode}
                    </div>
                    {cta}
                </div>
            </div>
        )
    }

    private renderSegment = (item: HotStoriesSiteItem, index: number) => {
        const { activeIndex, autoplayEnabled, progressKey } = this.state
        const { gallerySeconds } = this.props
        const isActive = index === activeIndex
        const isActiveVideo = isActive && this.isVideoItem(item)
        const showFullIndicator = !autoplayEnabled && isActive && !isActiveVideo
        const durationSec = autoplayEnabled && isActive && !isActiveVideo && gallerySeconds > 0 ? gallerySeconds : 0

        return (
            <button
                key={item.itemId || index}
                type="button"
                className={classNames("hotStoriesSegment", { active: isActive })}
                onClick={() => this.onSelectItem(index)}
                aria-label={item.title || `story ${index + 1}`}
                aria-current={isActive ? "true" : undefined}
            >
                <div
                    className="hotStoriesProgressTrack"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {isActiveVideo &&
                        <div
                            key={progressKey}
                            ref={this.setVideoProgressFill}
                            className="hotStoriesProgressFill synced"
                        />}
                    {showFullIndicator &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill synced"
                            style={{ width: "100%" }}
                        />}
                    {durationSec > 0 &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill"
                            style={{ animationDuration: `${durationSec}s` }}
                        />}
                </div>
            </button>
        )
    }

    public render() {
        const { items, backgroundColorComponent } = this.props
        const { activeIndex } = this.state
        if (!items || items.length === 0) {
            return null
        }

        const canSwipe = items.length > 1
        const sliderSettings = {
            dots: false,
            arrows: false,
            infinite: canSwipe,
            speed: 450,
            slidesToShow: 1,
            slidesToScroll: 1,
            swipe: canSwipe,
            touchMove: canSwipe,
            swipeToSlide: false,
            touchThreshold: 3,
            rtl: false,
            adaptiveHeight: false,
            waitForAnimate: true,
            useCSS: true,
            useTransform: true,
            cssEase: "ease-out",
            initialSlide: this.toSlickIndex(0),
            beforeChange: this.onBeforeChange,
            afterChange: this.onAfterChange,
            onSwipe: this.onSliderSwipe,
        }

        return (
            <div className="HotStoriesFrontMobile" style={{ backgroundColor: backgroundColorComponent || HOT_STORIES_BG }} ref={this.setComponentRoot}>
                <Slider
                    {...sliderSettings}
                    className="hotStoriesSlider"
                    ref={slider => { this.slider = slider }}
                >
                    {items.map((_, slickIndex) => {
                        const index = this.toLogicalIndex(slickIndex)
                        return this.renderStory(items[index], index)
                    })}
                </Slider>
                <div className="hotStoriesStrip">
                    <div className="hotStoriesStripList">
                        {items.map((item, index) => this.renderSegment(item, index))}
                    </div>
                </div>
            </div>
        )
    }
}
